import { Button, DialogActions, DialogContent } from "@material-ui/core";
import React, { useRef } from "react";
import { useStoreon } from "storeon/react";
import { useSnackbar } from "notistack";
import { DialogContextProps } from "@interfaces/System/DialogContextProps";
import { TableEvents, TableState } from "@store/table-module";
import { CreateTypeBindDto } from "@dto/SettingsDto/TypeBindDto/CreateTypeBindDto";
import { UpdateTypeBindDto } from "@dto/SettingsDto/TypeBindDto/UpdateTypeBindDto";
import { ModalContentLoading } from "@components/Common/Modal";
import { typeBindService } from "@services/Settings";
import { TypeBind } from "@interfaces/TypeBind";
import { TypeBindForm } from "@components/Forms/Settings";
import { useAsync, useLoading } from "@hooks";

const TypeBindDialogContext = ({ onClose, modalProps }: DialogContextProps) => {
    const itemId = modalProps?.itemId;

    const formRef = useRef<any>();
    const { enqueueSnackbar } = useSnackbar();
    const { dispatch: tableDispatch } = useStoreon<TableState, TableEvents>();
    const { loadingStatus: submitLoadingStatus, setLoadingStatus: setSubmitLoadingStatus } =
        useLoading("inactivity");

    const { data, loadingStatus } = useAsync<TypeBind>(typeBindService.getOneTypeBind, {
        functionArgs: [itemId],
        canLoad: !!itemId,
    });

    const invokeSubmit = () => {
        formRef.current.handleSubmit();
    };

    const handleCreateTypeBind = async (values) => {
        setSubmitLoadingStatus("loading");
        try {
            const dto = typeBindService.plainToClass(CreateTypeBindDto, values);

            await typeBindService.createTypeBind(dto);
            enqueueSnackbar("Настройка успешно обновлена", {
                variant: "success",
            });
            onClose();
            tableDispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при обновлении настройки", {
                variant: "error",
            });
        }
        setSubmitLoadingStatus("loaded");
    };

    const handleUpdateTypeBind = async (values) => {
        if (!itemId) return;

        setSubmitLoadingStatus("loading");
        try {
            const dto = typeBindService.plainToClass(UpdateTypeBindDto, values);

            await typeBindService.updateTypeBind(itemId, dto);
            enqueueSnackbar("Настройка успешно обновлена", {
                variant: "success",
            });
            onClose();
            tableDispatch("table/update/data");
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при обновлении настройки", {
                variant: "error",
            });
        }
        setSubmitLoadingStatus("loaded");
    };

    return (
        <>
            <DialogContent>
                {loadingStatus === "loading" && <ModalContentLoading />}

                {(loadingStatus === "loaded" || loadingStatus === "inactivity") && (
                    <TypeBindForm
                        ref={formRef}
                        onSubmit={itemId ? handleUpdateTypeBind : handleCreateTypeBind}
                        data={itemId ? data : undefined}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button color="default" onClick={onClose}>
                    Отмена
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={invokeSubmit}
                    disabled={submitLoadingStatus === "loading"}
                >
                    Сохранить
                </Button>
            </DialogActions>
        </>
    );
};

export default TypeBindDialogContext;
